/** 포인트 원장의 공유 계약. 앱(`A-26` · `A-26p` · `A-49` · `A-43d` · `A-43e`)과 웹(`W-26` · `W-70`)이
 * 같은 값을 읽는다.
 *
 * **저장 구조는 `apps/api/migrations/0014_point_ledger.sql`(M-28, 2026-10-25 사용자 승인)이다.** 그 표에
 * 쓰는 코드는 적용이 먼저다. 적용 여부는 `d1_migrations` 로 판단하고, 코드가 따라와야 하는 목록은
 * `docs/DATA_DICTIONARY.md` 48.9 에 있다. 이 파일의 타입과 순수 함수는 표가 없어도 컴파일된다.
 *
 * **근거**: PRD 머리 「2026-10-25 둘째 개정」 결정 1~10, `13.1` O-83 · O-89, 그리고 같은 날의 법무 최종
 * 검토 · 사용자 결정(메인 세션 전달): 차감은 어느 충전 묶음에서 나갔는지 남기고, 못 만든 장의 포인트는
 * **꺼낸 그 묶음으로** 돌아가며, 그 묶음이 7일 안이면 돌아온 포인트도 그 묶음의 현금 환불에 든다.
 *
 * **화면에 나가는 낱말은 이 파일에 없다.** 「포인트」 · 「충전」 · 「잔액」은 PRD `7.5.9` 용어 표에 아직
 * 없다(`13.1` O-86, `design-manager` 소관). 여기는 코드 값과 숫자만 둔다. 환불 불가 · 7일 문장도 법무
 * 판정(`13.1` O-90) 전에는 화면에 싣지 않는다. 이 파일의 `refundWindowOpen` 같은 값은 **판정 재료**이지
 * 문장이 아니다.
 *
 * ⛔ 소멸 · 만료 · 유효기간 값이 이 파일에 없다. 포인트는 사라지지 않는다. `refundableUntil` 은 현금
 * 환불 창구의 끝이지 포인트의 끝이 아니다.
 */

import { cutoutPrice } from "./cost";

/** 원장 한 줄의 종류. 정본은 `0014` 의 INSERT 지킴이 목록이고 이 배열과 **한 벌**이어야 한다.
 * 늘리려면 마이그레이션(트리거 교체)과 이 배열을 같은 PR 에서 바꾼다.
 * ⛔ `TRANSFER`(계정 사이 옮기기) · `EXPIRE`(소멸)는 없다. 넣지 않는다(2026-10-25 사용자 결정). */
export const POINT_ENTRY_KINDS = [
  "CHARGE",
  "SPEND",
  "RETURN",
  "SPEND_VOID",
  "CHARGE_REFUND",
  "CHARGE_REINSTATE",
  "ADJUST",
] as const;
export type PointEntryKind = (typeof POINT_ENTRY_KINDS)[number];

/** 묶음(lot)의 종류. `PURCHASED` = 스토어 충전 묶음(7일 현금 환불 창구가 있다), `GRANTED` = 운영자 지급
 * (창구 없음). 못 만든 장으로 돌아온 포인트는 **새 묶음이 아니다**. 꺼낸 묶음으로 돌아간다. */
export const POINT_LOT_CLASSES = ["PURCHASED", "GRANTED"] as const;
export type PointLotClass = (typeof POINT_LOT_CLASSES)[number];

export const STORE_PLATFORMS = ["APP_STORE", "GOOGLE_PLAY"] as const;
export type StorePlatform = (typeof STORE_PLATFORMS)[number];

/** 그 줄이 어디서 일어났나. 값은 코드가 지킨다(`0014` 에 CHECK 없음). */
export const POINT_CHANNELS = ["APP_IOS", "APP_ANDROID", "WEB", "SERVER", "OPERATOR"] as const;
export type PointChannel = (typeof POINT_CHANNELS)[number];

/** 충전한 날부터 현금 환불을 받을 수 있는 날수(결정 8 (b)). **이 상수 한 벌**이 `PointEntry.refundableUntil`
 * 을 정한다. DB 는 그 값이 구매 시각보다 이르지 않은지만 본다. 법무 판정(O-90)으로 바뀌면 여기만 바꾼다.
 * ⚠️ 이미 적힌 묶음의 `refundableUntil` 은 그때 값으로 남는다(원장은 고치지 않는다). */
export const POINT_CHARGE_REFUND_WINDOW_DAYS = 7;

/** 장 하나에 쓰는 포인트(₩). 장당 값은 `cost.ts` 한 곳에서만 곱한다. */
export function pointsForImages(imageCount: number): number {
  return cutoutPrice(imageCount);
}

// ── 잔액과 묶음 ───────────────────────────────────────────────────────────────

/** 잔액 요약(`A-26` 남은 포인트 · `A-43d` 머리 · `W-26` 포인트 쓰기 칸).
 *
 * **모든 수는 서버가 원장에서 낸 것이다.** 화면이 더하거나 빼지 않는다. 등식은 서버 쪽에서 선다:
 * `balance = Σ 묶음 남은 양 − debt` (`docs/DATA_DICTIONARY.md` 48.7 질의 ①). */
export interface PointBalanceView {
  /** 원장의 합. 스토어 환불로 빚이 생기면 음수일 수 있다. */
  balance: number;
  /** 지금 현금 환불 창구 안에 있는 몫. 창구가 열린 충전 묶음들의 남은 양 합. `balance` 를 넘지 않는다. */
  refundableNow: number;
  /** 갚지 못한 빚(≥ 0). 0 보다 크면 `canSpend` 는 거짓이다. */
  debt: number;
  /** 지금 차감할 수 있는가. 계정이 정상이고 `balance > 0`. 서버 지킴이와 같은 판정이다. */
  canSpend: boolean;
}

/** 충전 묶음 하나(`A-43d` 충전 줄 · 충전 환불 요청 자리). */
export interface PointChargePackView {
  /** 원장 줄 id(CHARGE). 환불 되돌림(CHARGE_REINSTATE)은 새 묶음이 아니라 이 묶음으로 돌려 넣는다. */
  entryId: string;
  platform: StorePlatform;
  /** 스토어가 적은 구매 시각(ISO). */
  chargedAt: string;
  amount: number;
  /** 남은 양 = 충전 − 순 사용(쓴 것 − 돌아온 것) − 스토어 환불로 거둔 것. */
  remaining: number;
  /** 현금 환불 창구의 끝(ISO). ⛔ 포인트의 기한이 아니다. */
  refundableUntil: string;
  /** 지금 창구가 열려 있나(서버 시계 기준). 화면이 시계를 다시 재지 않는다(36장). */
  refundWindowOpen: boolean;
  /** 스토어 쪽 상태. `REVOKED` 면 환불된 구매다. */
  storeState: "VERIFIED" | "REVOKED" | "REINSTATED";
}

/** 내역 한 줄(`A-43d`).
 * ⚠️ 목업의 갈래는 셋(충전 · 씀 · 돌아옴)이다. 나머지 종류(`SPEND_VOID` · `CHARGE_REFUND` ·
 * `CHARGE_REINSTATE` · `ADJUST`)를 어느 갈래에 둘지, 보일지는 화면 결정이다(`13.1` O-88). 그래서
 * 이 줄은 종류 값을 그대로 싣고 갈래는 `pointHistoryGroup` 이 아는 것만 낸다. */
export interface PointHistoryItemView {
  entryId: string;
  kind: PointEntryKind;
  /** 부호 있는 금액(₩). */
  amount: number;
  /** ISO. */
  occurredAt: string;
  /** 작업에 걸린 줄(SPEND · RETURN · SPEND_VOID)이면 그 작업. */
  jobId: string | null;
  productName: string | null;
  /** SPEND 면 장 수, RETURN 이면 1. 그 밖은 null. */
  imageCount: number | null;
  /** 충전 줄만. 스토어와 창구. */
  platform: StorePlatform | null;
  refundableUntil: string | null;
}

export type PointHistoryGroup = "CHARGE" | "SPEND" | "RETURN";

/** 내역 거르기 갈래. 목업 `A-43d` 의 셋에 정해진 종류만 매긴다. 그 밖은 `null`(화면 결정 전). */
export function pointHistoryGroup(kind: PointEntryKind): PointHistoryGroup | null {
  switch (kind) {
    case "CHARGE":
      return "CHARGE";
    case "SPEND":
      return "SPEND";
    case "RETURN":
      return "RETURN";
    default:
      return null;
  }
}

// ── 웹 결제의 포인트 몫과 토스 몫 ─────────────────────────────────────────────

/** `W-26` 에서 셀러가 고르는 셋(결정 5). 처음에는 아무것도 골라져 있지 않다(목업). */
export type WebPointChoice = "POINTS_ONLY" | "POINTS_AND_TOSS" | "TOSS_ONLY";

/** 한 결제의 두 몫. 저장 쪽에서는 포인트 몫 = 그 결제 SPEND 의 |amount|, 토스 몫 =
 * `Payment.authorizedAmount` 이고, 둘의 합이 작업 값이다(48.6 · 48.7 질의 ③). */
export interface PaymentPointSplit {
  pointAmount: number;
  tossAmount: number;
}

/** 고른 갈래가 지금 잔액으로 서는가, 서면 두 몫은 얼마인가. 서지 않으면 `null`.
 *
 * 목업 `W-26` 규칙 그대로다. 포인트로만 = 잔액 ≥ 합계일 때, 포인트 모두 쓰고 나머지는 결제 수단으로 =
 * 0 < 잔액 < 합계일 때(잔액을 전부 쓴다. 금액 직접 적기는 없다), 결제 수단으로만 = 언제나.
 * 잔액이 0 이하이면(빚 포함) 포인트 갈래는 서지 않는다.
 * ⚠️ ₩500 단위로 끊을지(잔액 ₩700 을 섞을 때 ₩700 을 쓰는가 ₩500 을 쓰는가)는 `13.1` O-89 의 남은
 * 물음이다. 이 함수는 목업대로 **잔액 전부**를 쓴다. 답이 바뀌면 여기만 바꾼다.
 * 서버는 승인 때 같은 함수로 `토스 승인 금액 + 포인트 몫 = 합계` 를 대조한다. */
export function planWebPointSplit(
  total: number,
  balance: number,
  choice: WebPointChoice,
): PaymentPointSplit | null {
  if (total <= 0) return null;
  switch (choice) {
    case "TOSS_ONLY":
      return { pointAmount: 0, tossAmount: total };
    case "POINTS_ONLY":
      return balance >= total ? { pointAmount: total, tossAmount: 0 } : null;
    case "POINTS_AND_TOSS":
      return balance > 0 && balance < total
        ? { pointAmount: balance, tossAmount: total - balance }
        : null;
  }
}

/** 못 만든 장 한 장의 금액이 어디로 돌아가는가. **토스(카드) 몫부터, 그다음 포인트**(결정 7b).
 *
 * @param unitPrice 그 결제의 장당 단가(`Payment.unitPriceAmount`). 오늘 단가를 곱하지 않는다.
 * @param tossRemaining 그 결제의 토스 몫 가운데 아직 어느 장에도 잡히지 않은 금액
 *   (`Payment.authorizedAmount − Σ ImageRefund.amount`). 음수면 0 으로 본다.
 *
 * 서버는 이 값을 결판 묶음 **안에서** 다시 센다(묶음끼리는 직렬이라 둘째 장은 첫째가 잡은 몫을 본다).
 * DB 지킴이가 같은 규칙을 다시 막는다(`0014` RETURN · `ImageRefund_point_payment_guard`). */
export function splitUnmadeImageReturn(
  unitPrice: number,
  tossRemaining: number,
): PaymentPointSplit {
  const tossAmount = Math.min(unitPrice, Math.max(0, tossRemaining));
  return { tossAmount, pointAmount: unitPrice - tossAmount };
}

// ── 스토어 충전 확인 요청 ─────────────────────────────────────────────────────

/** 앱이 스토어 결제를 마친 뒤 서버에 확인을 맡기는 요청. 서버가 스토어 API 로 다시 확인하고, 거래의
 * 계정 표지(Apple `appAccountToken` · Play `obfuscatedExternalAccountId`)가 이 셀러의 것일 때만 충전한다.
 * ⛔ 앱이 보낸 금액 · 상품 값을 믿지 않는다. 금액은 서버의 상품 대응표가 정한다(`13.1` O-84). */
export type PointChargeVerifyRequest =
  | { platform: "APP_STORE"; transactionId: string }
  | { platform: "GOOGLE_PLAY"; productId: string; purchaseToken: string };

export interface PointChargeVerifyResponse {
  /** 이번 요청이 새로 충전했나. 같은 거래를 다시 보내면 `false` 이고 잔액은 그대로다. */
  credited: boolean;
  pack: PointChargePackView;
  balance: PointBalanceView;
}

/** 앱이 스토어 결제 시트를 열기 전에 받는 계정 표지(UUID). 계정마다 하나이고 바뀌지 않는다. */
export interface PointStoreAccountTokenView {
  storeAccountToken: string;
}
